import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, CircularProgress, Typography, Dialog, Fab } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { createAudioAnalyzer, getFrequencyData, drawWaveform } from './audioUtils';

/**
 * Voice Recorder Component with 4-second burst recording
 */
const VoiceRecorder = ({ onTranscription, currentUserId, validationType = 'grocery', children, autoStart = false }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const MAX_DURATION = 10; // seconds
    
    const { enqueueSnackbar } = useSnackbar();
    
    const mediaRecorderRef = useRef(null);
    const streamRef = useRef(null);
    const audioChunksRef = useRef([]);
    const canvasRef = useRef(null);
    const analyzerRef = useRef(null);
    const animationFrameRef = useRef(null);
    const audioContextRef = useRef(null);
    const timerIntervalRef = useRef(null);
    const hasAutoStartedRef = useRef(false);
    const recognitionRef = useRef(null);
    const finalTranscriptRef = useRef('');
    const isUsingWebSpeechRef = useRef(false);

    // Check for browser support
    // const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition; // Moved inside function

    // Cleanup resources
    const cleanupMicrophoneResources = () => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close().catch(e => console.error("Error closing audio context", e));
            audioContextRef.current = null;
        }

        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }

        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (e) {
                // Ignore errors if already stopped
            }
            recognitionRef.current = null;
        }

        analyzerRef.current = null;
    };

    useEffect(() => {
        return () => {
            cleanupMicrophoneResources();
        };
    }, []);

    // Auto-start effect
    useEffect(() => {
        if (autoStart && !isRecording && !isTranscribing && !isOverlayOpen && !hasAutoStartedRef.current) {
            hasAutoStartedRef.current = true;
            handleStartRecording();
        }
    }, [autoStart]);

    // Effect to start visualization once the overlay is open and canvas is potentially ready
    useEffect(() => {
        if (isRecording && isOverlayOpen && streamRef.current) {
            // Give a small delay for Dialog to mount the canvas
            const timeoutId = setTimeout(() => {
                startVisualization(streamRef.current);
            }, 100);
            return () => clearTimeout(timeoutId);
        }
    }, [isRecording, isOverlayOpen]);

    const startVisualization = (stream) => {
        try {
            if (!audioContextRef.current) {
                const { audioContext, analyzer } = createAudioAnalyzer(stream);
                audioContextRef.current = audioContext;
                analyzerRef.current = analyzer;
            }
            
            const visualize = () => {
                // If canvas isn't ready yet, keep looping until it is
                if (!canvasRef.current) {
                    animationFrameRef.current = requestAnimationFrame(visualize);
                    return;
                }
                
                if (analyzerRef.current) {
                    const frequencyData = getFrequencyData(analyzerRef.current);
                    drawWaveform(canvasRef.current, frequencyData, true, {
                        bgColor: 'transparent',
                        barColor: '#ffffff',
                        centerLineColor: 'rgba(255, 255, 255, 0.5)'
                    });
                }
                
                animationFrameRef.current = requestAnimationFrame(visualize);
            };
            
            visualize();
        } catch (error) {
            console.error('Error starting visualization:', error);
        }
    };

    const handleStartRecording = async () => {
        cleanupMicrophoneResources();
        setRecordingDuration(0);
        setIsOverlayOpen(true);
        finalTranscriptRef.current = '';
        isUsingWebSpeechRef.current = false;

        // Try Web Speech API first
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            try {
                const recognition = new SpeechRecognition();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = 'en-US';

                recognition.onresult = (event) => {
                    let transcript = '';
                    for (let i = event.resultIndex; i < event.results.length; ++i) {
                        transcript += event.results[i][0].transcript;
                    }
                    if (transcript.trim()) {
                        finalTranscriptRef.current = transcript;
                    }
                };

                recognition.onerror = (event) => {
                    console.warn('Speech recognition error, falling back:', event.error);
                    // If we haven't started recording for real yet or failed immediately, try fallback
                    // But if we are already deep in, just let it fail gracefully or rely on what we have?
                    // For simplicity, if error occurs, we might just stop.
                    // Ideally, we catch this before "starting" effectively.
                };

                recognitionRef.current = recognition;
                if (recognition && typeof recognition.start === 'function') {
                    recognition.start();
                } else {
                    throw new Error(`recognition.start is not a function. Type: ${typeof recognition?.start}`);
                }
                isUsingWebSpeechRef.current = true;
                setIsRecording(true);

                // Still try to get the stream for VISUALIZATION only
                // If this fails, we just don't show the component, but transcription still works
                navigator.mediaDevices.getUserMedia({ audio: true })
                    .then(stream => {
                        streamRef.current = stream;
                        // Trigger visualization
                        const timeoutId = setTimeout(() => {
                            startVisualization(stream);
                        }, 100);
                    })
                    .catch(e => console.warn('Visualization stream failed', e));

                startTimer();
                return;

            } catch (e) {
                console.error('Web Speech API failed to start:', e);
                // Fall through to fallback
            }
        }

        // Fallback or if Web Speech unavailable
        await startFallbackRecording();
    };


    const startFallbackRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            
            audioChunksRef.current = [];
            mediaRecorderRef.current = new MediaRecorder(stream);
            
            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorderRef.current.onstop = handleFallbackRecordingComplete;
            
            mediaRecorderRef.current.start();
            setIsRecording(true);
            isUsingWebSpeechRef.current = false;
            
            // visualization started via useEffect or direct call
             const timeoutId = setTimeout(() => {
                startVisualization(stream);
            }, 100);
            
            startTimer();
            
        } catch (error) {
            console.error('Error accessing microphone:', error);
            enqueueSnackbar('Microphone access denied. Please enable microphone permissions.', { 
                variant: 'error' 
            });
            setIsOverlayOpen(false);
        }
    };

    const startTimer = () => {
         const startTime = Date.now();
         timerIntervalRef.current = setInterval(() => {
            const elapsed = (Date.now() - startTime) / 1000;
            setRecordingDuration(elapsed);
            
            if (elapsed >= MAX_DURATION) {
                stopRecording(true); 
            }
        }, 100);
    };

    const stopRecording = (arg) => {
        const isTimeout = arg === true;
        if (isUsingWebSpeechRef.current) {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
            handleWebSpeechComplete(isTimeout);
        } else {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            // State update handled in onstop -> handleFallbackRecordingComplete
        }
    };

    const handleManualCancel = () => {
        cleanupMicrophoneResources();
        setIsRecording(false);
        setIsOverlayOpen(false);
        setIsTranscribing(false);
    };

    const handleWebSpeechComplete = (isTimeout = false) => {
        setIsRecording(false);
         if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        const text = finalTranscriptRef.current;
        cleanupMicrophoneResources();

         if (text && text.trim()) {
            onTranscription(text.trim(), currentUserId);
            enqueueSnackbar('Item added!', { variant: 'success' });
            setIsOverlayOpen(false);
        } else {
             // If web speech got nothing...
             if (isTimeout) {
                 // If timeout w/ no speech, just close (user likely afk or silent)
                 enqueueSnackbar("No speech detected.", { variant: 'info' });
                 setIsOverlayOpen(false);
             } else {
                 // Manual stop w/ no speech -> offer retry
                 enqueueSnackbar("Didn't catch that. Tap 'Check' when done speaking.", { variant: 'info' });
                 setRecordingDuration(0);
             }
        }
    };

    const handleFallbackRecordingComplete = async () => {
        setIsRecording(false); // Update UI state
        
        // Stop the visualization loop
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        // If user cancelled manually (overlay closed), don't transcribe
        // Note: checking ref or state here might be tricky due to closures, 
        // but handleManualCancel closes overlay immediately.
        // We can check local variable driven logic if needed, but state should be fine.
        
        setIsTranscribing(true);
        
        try {
            const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
            
            if (audioBlob.size === 0) {
                // If it was a super short click, maybe no data?
                // But usually 4 seconds produces data.
                if (isOverlayOpen) {
                    enqueueSnackbar('No audio recorded. Please try again.', { variant: 'warning' });
                }
                setIsTranscribing(false);
                setIsOverlayOpen(false);
                return;
            }
            
            const reader = new FileReader();
            const base64Audio = await new Promise((resolve, reject) => {
                reader.onloadend = () => {
                    const base64 = reader.result.split(',')[1];
                    resolve(base64);
                };
                reader.onerror = reject;
                reader.readAsDataURL(audioBlob);
            });
            
            const model = 'gpt-4o-mini-transcribe';
            const functionUrl = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 
                'https://us-central1-photo-organization-1e84f.cloudfunctions.net/transcribe_audio';
            
            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    audio: base64Audio, 
                    model: model,
                    validation_type: validationType
                })
            });

            if (!response.ok) throw new Error('Transcription failed');
            
            const data = await response.json();
            
            if (data.valid === false && data.error_message) {
                enqueueSnackbar(data.error_message, { variant: 'warning' });
            } else if (data.cleaned_text && data.cleaned_text.trim()) {
                onTranscription(data.cleaned_text, currentUserId);
                enqueueSnackbar('Item added!', { variant: 'success' });
            } else if (data.text && data.text.trim()) {
                onTranscription(data.text, currentUserId);
                enqueueSnackbar('Item added!', { variant: 'success' });
            } else {
                enqueueSnackbar('No speech detected.', { variant: 'warning' });
            }
            
        } catch (error) {
            console.error('Error transcribing:', error);
            enqueueSnackbar('Transcription failed. Please try again.', { variant: 'error' });
        } finally {
            setIsTranscribing(false);
            setIsOverlayOpen(false);
            cleanupMicrophoneResources(); // Final cleanup
        }
    };

    const handleRetry = () => {
         handleStartRecording();
    };

    return (
        <>
            {children ? (
                <Box onClick={isTranscribing ? undefined : handleStartRecording} sx={{ width: '100%', cursor: isTranscribing ? 'not-allowed' : 'pointer' }}>
                    {children}
                </Box>
            ) : (
                <IconButton
                    onClick={handleStartRecording}
                    disabled={isTranscribing}
                    sx={{
                        width: 120,
                        height: 120,
                        bgcolor: 'white',
                        color: '#ed8936',
                        boxShadow: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            transform: 'scale(1.05)'
                        },
                        '&:disabled': {
                            bgcolor: 'rgba(255, 255, 255, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)'
                        }
                    }}
                >
                    {isTranscribing ? <CircularProgress size={60} sx={{ color: '#ed8936' }} /> : <MicIcon sx={{ fontSize: 60 }} />}
                </IconButton>
            )}

            {/* Full Screen Overlay */}
            <Dialog
                fullScreen
                open={isOverlayOpen}
                onClose={handleManualCancel}
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(0, 0, 0, 0.65)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                    }
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={handleManualCancel}
                    sx={{
                        position: 'absolute',
                        top: 40,
                        right: 40,
                        color: 'white',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)'
                        }
                    }}
                >
                    <CloseIcon fontSize="large" />
                </IconButton>

                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    gap: 6,
                    width: '100%',
                    maxWidth: 600,
                    p: 4
                }}>
                    
                    {/* Status Text */}
                    <Typography
                        variant="h4"
                        sx={{
                            color: 'white',
                            fontWeight: 600,
                            letterSpacing: 1,
                            opacity: 0.9
                        }}
                    >
                        {isTranscribing ? 'Processing...' : 'Listening...'}
                    </Typography>

                    {/* Countdown / Progress */}
                    {isTranscribing ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CircularProgress size={120} thickness={2} sx={{ color: 'white' }} />
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ 
                                position: 'relative', 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'center', 
                                gap: 2 
                            }}>
                                
                                {/* Timer Display */}
                                <Typography
                                    sx={{
                                        fontSize: '4rem',
                                        fontWeight: 300,
                                        color: 'rgba(255,255,255,0.9)',
                                        fontVariantNumeric: 'tabular-nums',
                                        letterSpacing: 2
                                    }}
                                >
                                    0:0{Math.min(Math.floor(recordingDuration), 9)}
                                </Typography>

                                {/* Waveform Visualization */}
                                <Box 
                                    onClick={stopRecording}
                                    sx={{ 
                                        width: '100%', 
                                        height: 160, 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <canvas
                                        ref={canvasRef}
                                        width={window.innerWidth > 600 ? 600 : window.innerWidth - 40}
                                        height={160}
                                        style={{
                                            width: '100%',
                                            height: '100%'
                                        }}
                                    />
                                </Box>

                                {/* Done Button */}
                                <Fab
                                    aria-label="finish recording"
                                    color="primary"
                                    onClick={stopRecording}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        bgcolor: 'white',
                                        color: '#ed8936',
                                        '&:hover': {
                                            bgcolor: '#f7fafc',
                                            transform: 'scale(1.05)'
                                        },
                                        boxShadow: '0 0 20px rgba(255,255,255,0.3)',
                                        animation: 'pulse 2s infinite'
                                    }}
                                >
                                    <CheckIcon sx={{ fontSize: 40 }} />
                                </Fab>

                                    

                            </Box>
                        </>
                    )}
                    
                    {!isRecording && !isTranscribing && (
                        /* Retry State (if needed, though we auto-restart usually or close) */
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                             <Typography variant="h5" sx={{ color:'white', mb: 3 }}>
                                Ready?
                             </Typography>
                             <IconButton
                                onClick={handleRetry}
                                sx={{
                                    width: 80,
                                    height: 80,
                                    bgcolor: '#ed8936',
                                    color: 'white',
                                    '&:hover': { bgcolor: '#dd6b20' }
                                }}
                             >
                                <MicIcon sx={{ fontSize: 40 }} />
                             </IconButton>
                        </Box>
                    )}
                    

                </Box>
            </Dialog>
        </>
    );
};

export default VoiceRecorder;
